import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useRef, useState } from "react";
import { tokens } from "../../../../theme";
import { usePermissions } from "../../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../../constants/permissions";
import { RECIPIENT_USER_OPTIONS } from "../data/recipientUserOptions";
import { NotificationGroup, NotificationTemplateFormValues } from "../types/types";
import LivePreviewPanel from "./LivePreviewPanel";
import RecipientGroupsSelector from "./RecipientGroupsSelector";
import VariableChips from "./VariableChips";

interface NotificationTemplateFormProps {
  initialValues: NotificationTemplateFormValues;
  groups: NotificationGroup[];
  submitLabel: string;
  submittingLabel: string;
  onSubmit: (values: NotificationTemplateFormValues) => void;
  submitting?: boolean;
}

export default function NotificationTemplateForm({
  initialValues,
  groups,
  submitLabel,
  submittingLabel,
  onSubmit,
  submitting = false,
}: NotificationTemplateFormProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(PERMISSIONS.CONFIGURATION_CHANGE);
  const [values, setValues] = useState<NotificationTemplateFormValues>(initialValues);
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const descriptionInputRef = useRef<HTMLTextAreaElement | null>(null);

  const update = <K extends keyof NotificationTemplateFormValues>(
    key: K,
    value: NotificationTemplateFormValues[K],
  ) => setValues((prev) => ({ ...prev, [key]: value }));

  // Inserts at the cursor position (or selection) instead of always
  // appending, so a variable can be dropped mid-sentence.
  const insertVariable = (
    key: "titleTemplate" | "descriptionTemplate",
    inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>,
    variable: string,
  ) => {
    const el = inputRef.current;
    const current = values[key];

    if (!el) {
      update(key, `${current}${variable}`);
      return;
    }

    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const next = `${current.slice(0, start)}${variable}${current.slice(end)}`;
    update(key, next);

    requestAnimationFrame(() => {
      el.focus();
      const cursor = start + variable.length;
      el.setSelectionRange(cursor, cursor);
    });
  };

  return (
    <Box sx={{ p: 5, bgcolor: colors.primary[900], minHeight: "100vh", borderRadius: 5 }}>
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Typography sx={{ fontWeight: 700, color: colors.primary[700], mb: 2 }}>
            Basic Information
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
            <TextField
              label="Template Name"
              value={values.name}
              onChange={(e) => update("name", e.target.value)}
              fullWidth
            />
            <TextField
              label="Trigger Source"
              value={values.triggerSource || "-"}
              fullWidth
              InputProps={{ readOnly: true }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={values.isActive}
                  onChange={(e) => update("isActive", e.target.checked)}
                />
              }
              label="Active"
            />
          </Box>

          <Divider sx={{ mb: 3, borderColor: colors.primary[800] }} />

          <Typography sx={{ fontWeight: 700, color: colors.primary[700], mb: 1.5 }}>
            Recipients
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
            <TextField
              select
              label="Recipient User"
              value={values.recipientUser}
              onChange={(e) => update("recipientUser", e.target.value)}
              fullWidth
            >
              {RECIPIENT_USER_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <RecipientGroupsSelector
              groups={groups}
              selectedGroupIds={values.recipientGroups}
              onChange={(ids) => update("recipientGroups", ids)}
              colors={colors}
            />
          </Box>

          <Divider sx={{ mb: 3, borderColor: colors.primary[800] }} />

          <Typography sx={{ fontWeight: 700, color: colors.primary[700], mb: 1.5 }}>
            Notification Content
          </Typography>
          <Box sx={{ mb: 2 }}>
            <TextField
              label="Title Template"
              value={values.titleTemplate}
              onChange={(e) => update("titleTemplate", e.target.value)}
              inputRef={titleInputRef}
              fullWidth
              placeholder="e.g. New booking #{{ booking.id }}"
            />
            <VariableChips
              triggerSource={values.triggerSource}
              onInsert={(variable) => insertVariable("titleTemplate", titleInputRef, variable)}
              colors={colors}
              label="Insert into title"
            />
          </Box>
          <Box sx={{ mb: 4 }}>
            <TextField
              label="Description Template"
              value={values.descriptionTemplate}
              onChange={(e) => update("descriptionTemplate", e.target.value)}
              inputRef={descriptionInputRef}
              multiline
              minRows={3}
              fullWidth
              placeholder="e.g. {{ customer.name }} has a new booking with reference {{ booking.reference_number }}."
            />
            <VariableChips
              triggerSource={values.triggerSource}
              onInsert={(variable) => insertVariable("descriptionTemplate", descriptionInputRef, variable)}
              colors={colors}
              label="Insert into description"
            />
          </Box>

          {canEdit && (
            <Button
              variant="contained"
              disabled={submitting || !values.name.trim()}
              onClick={() => onSubmit(values)}
              sx={{
                bgcolor: colors.primary[400],
                "&:hover": { bgcolor: colors.primary[500] },
                borderRadius: 2,
                textTransform: "none",
                px: 4,
                py: 1.25,
              }}
            >
              {submitting ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                submitLabel
              )}
            </Button>
          )}
          {submitting && (
            <Typography variant="caption" sx={{ ml: 2, color: colors.primary[300] }}>
              {submittingLabel}
            </Typography>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Paper
            elevation={0}
            sx={{
              position: "sticky",
              top: 24,
              p: 3,
              borderRadius: 4,
              border: `1px solid ${colors.primary[800]}`,
              bgcolor: colors.primary[900],
            }}
          >
            <LivePreviewPanel
              title={values.titleTemplate}
              message={values.descriptionTemplate}
              colors={colors}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
